import Promise from 'bluebird';
import { isIOS, isAndroidChrome } from '../../../helpers/serverRenderingUtils';
import AnchorAPIError from './AnchorAPIError';
import { fetchMoneyWallet } from './fetchMoneyWallet';
import { fetchPodcastStatus } from './fetchPodcastStatus';
import { optOutOfPodcastDistribution } from './optOutOfPodcastDistribution';
import { fetchEpisodes } from './fetchEpisodes.ts';
import { deleteEpisode } from './deleteEpisode.ts';
import { fetchCurrentUsersPodcast } from './fetchCurrentUsersPodcast.ts';
import { fetchCurrentPodcastsPlayData } from './fetchCurrentPodcastsPlayData.ts';
import { fetchCurrentPodcastsWallet } from './fetchCurrentPodcastsWallet.ts';
import { fetchCurrentPodcastPlaysByDateRange } from './fetchCurrentPodcastPlaysByDateRange.ts';
import { fetchCurrentPodcastsTopPlayedEpisodes } from './fetchCurrentPodcastsTopPlayedEpisodes.ts';
import { fetchCurrentPodcastsPlaysByApp } from './fetchCurrentPodcastsPlaysByApp.ts';
import { fetchCurrentPodcastsPlaysByDevice } from './fetchCurrentPodcastsPlaysByDevice.ts';
import { fetchCurrentPodcastsPlaysByGeo } from './fetchCurrentPodcastsPlaysByGeo.ts';
import {
  fetchUserDeletionRequest,
  requestUserDeletion,
} from './userDeletionRequest.ts';
import { fetchEpisodeTotalPlayCount } from './fetchEpisodeTotalPlayCount';
import { fetchEpisodePlaysByDateRange } from './fetchEpisodePlaysByDateRange';
import { fetchEpisodePlaysComparison } from './fetchEpisodePlaysComparison';
import { fetchLogin } from './fetchLogin.ts';
import { fetchSignupAndLogin } from './fetchSignupAndLogin.ts';
import { updateCurrentUserEmail } from './updateCurrentUserEmail.ts';
import { createVoiceMessage } from './createVoiceMessage.ts';
import { logoutCurrentUser } from './logoutCurrentUser.ts';
import { fetchAudioGroup, fetchBackgroundTracks } from './fetchAudioGroup.ts';
import { fetchAudioLibrary } from './fetchAudioLibrary.ts';
import { transformAudioWithBackgroundTrack } from './transformAudioWithBackgroundTrack.ts';
import { fetchBackgroundTrackOfAudio } from './fetchBackgroundTrackOfAudio.ts';
import { retryAudioTransformation } from './retryAudioTransformation.ts';
import { fetchPodcastCategories } from './fetchPodcastCategories.ts';
import { updateTranscription } from './updateTranscription.ts';
import { generateVideoTranscriptionRequest } from './generateVideoTranscriptionRequest.ts';
import { fetchVideoTranscriptionRequestStatus } from './fetchVideoTranscriptionRequestStatus.ts';
import { hideCallIn } from './hideCallIn.ts';
import { updatePodcastMetadata } from './updatePodcastMetadata.ts';
import { fetchPodcastMetadata } from './fetchPodcastMetadata.ts';
import { updateSocialUrls } from './updateSocialUrls.ts';
import { fetchSocialUrls } from './fetchSocialUrls';

import AnchorErrorTracking from '../AnchorErrorTracking';

import trackEvent from '../analytics/trackEvent';

const convertDataToFormData = data => {
  const formData = new FormData();

  for (const k in data) {
    if (k === 'file') {
      const isFileValueAnArray = Array.isArray(data[k]);
      const file = isFileValueAnArray ? data[k][0] : data[k];
      if (isFileValueAnArray) {
        const fileValueArrayIncludesFilename = !!data[k][1];
        if (fileValueArrayIncludesFilename) {
          const fileName = data[k][1];
          formData.append(k, file, fileName);
        }
      }
      formData.append(k, file);
    } else {
      formData.append(k, data[k]);
    }
  }
  return formData;
};

const AnchorAPI = {
  cancelSupportForStation: (stationId, cancelCode) =>
    new Promise((resolve, reject) => {
      fetch(`/api/station/${stationId}/support/cancel`, {
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        method: 'POST',
        body: JSON.stringify({
          cancelCode,
        }),
      })
        .then(response => {
          if (response.ok) {
            resolve(response);
          } else {
            new AnchorAPIError(
              response,
              `Non 200 response status: ${response.status}`
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  getStationIdForVanityName: vanitySlug =>
    new Promise((resolve, reject) => {
      fetch(`/api/vanityslug?url=${vanitySlug}`, {
        credentials: 'same-origin',
        method: 'GET',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              const { stationId } = responseJSON;
              if (stationId) {
                resolve(stationId, responseJSON);
              } else {
                reject(new AnchorAPIError(response, `No station found.`));
              }
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  // Note: The onUploadProgressUpdate callback represents the status of the file
  //       or blob uploading to the server and does not include the processing. So
  //       for example, you may get a callback will 100 (100%) complete, this represents
  //       that file or blob has been upload to the server, but it doesn't mean the request
  //       is done, because the next step is processing. The request stays open until
  //       this processing is complete.
  //       TLDR: 100% does not represent the request is complete, and sometimes, depeneding
  //             on how much processing needs to happen, the request can stay open for a long
  //             time after 100% upload is complete, because the processing is happening.
  uploadAudioFile: (
    fileOrBlob,
    onUploadProgressUpdated = () => null,
    onProcessingDidStart = () => null,
    caption,
    options = {}
  ) =>
    new Promise((resolve, reject) => {
      const isFile = typeof fileOrBlob.name === 'string';
      const isBlob = !isFile;

      const getFileNameForBlob = blob => {
        const fileType = blob.type === 'audio/wav' ? 'wav' : 'webm';
        const fileName = `blob-${Date.now()}.${fileType}`;
        return fileName;
      };
      const getFileNameForFile = file => {
        const fileName = file.name.replace(/[^a-zA-Z0-9-\.]/g, '-'); // unix-safe file path name
        return fileName;
      };
      // NOTE: Should we set caption
      const path = isBlob ? '/api/audio/recording' : '/api/audio/file';
      const fileName = isBlob
        ? getFileNameForBlob(fileOrBlob)
        : isFile
        ? getFileNameForFile(fileOrBlob)
        : '';

      const data = {
        file: [fileOrBlob, fileName],
        ...(caption ? { caption } : {}),
        ...options,
      };
      const formData = convertDataToFormData(data);
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener(
        'progress',
        evt => {
          if (evt.lengthComputable) {
            const percentComplete = parseInt(
              100 * (evt.loaded / evt.total),
              10
            );
            onUploadProgressUpdated(percentComplete);
            if (percentComplete === 100) {
              onProcessingDidStart();
            }
          }
        },
        false
      );
      xhr.open('POST', path);
      xhr.onload = function onXhrLoad(data) {
        // What should be here
        if (xhr.status === 200) {
          try {
            resolve(JSON.parse(xhr.response));
          } catch (err) {
            reject(err);
          }
        }
        if (xhr.status === 401) {
          reject(
            new AnchorAPIError(
              {}, // This is usually the response object. What should it be?
              `Non 200 response status: ${xhr.status}`
            )
          );
        }
      };
      // TODO: new error state. (usually CORS problem)
      xhr.onerror = reject;

      xhr.send(formData);
    }),
  // TODO: this name should be fetchPodcastProfile
  fetchPodcastProfileForStation: stationId =>
    new Promise((resolve, reject) => {
      fetch(`/api/v3/profile/${stationId}`, {
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  getPodcastMetadata: stationId =>
    new Promise((resolve, reject) => {
      fetch(`/api/v3/profile/${stationId}`, {
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              const { podcastMetadata } = responseJSON;
              resolve(podcastMetadata);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  getGeoRegion: () =>
    new Promise((resolve, reject) => {
      fetch('/api/compliance/region')
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              const { geoRegion, error } = responseJSON;
              if (typeof error !== 'undefined') {
                AnchorErrorTracking.captureException(error);
                reject(response);
              }
              if (typeof geoRegion !== 'undefined') {
                resolve(responseJSON);
              } else {
                AnchorErrorTracking.captureException('No region found.');
                reject(response);
              }
            });
          } else {
            reject(
              new Error(`${String(response.status)} response returned.`),
              response
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  getStation: stationId =>
    new Promise((resolve, reject) =>
      fetch(`/api/station/${stationId}`, {
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              const { stations } = responseJSON;
              const station = stations[0];
              resolve(station);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    ),
  getUserEmailAddress: () =>
    new Promise((resolve, reject) =>
      fetch('/api/user/email', {
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              const { emailAddress } = responseJSON;
              resolve(emailAddress);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    ),
  removeAllMusicInEpisodesForUser: () =>
    new Promise((resolve, reject) =>
      fetch(`/api/user/removemusic`, {
        credentials: 'same-origin',
        method: 'POST',
      })
        .then(response => {
          if (response.status === 200) {
            resolve();
            return;
          }
          reject(
            new AnchorAPIError(
              response,
              `Non 200 response status: ${response.status}`
            )
          );
        })
        .catch(err => {
          reject(err);
        })
    ),
  updatePodcastMetadataDeprecated: (data = {}) => {
    // TODO: the backend expects data.externalLinks and it chokes if you dont send it. We should modify the backend to handle the request when externalLinks doesn't exists. For now, we're handling it below.
    // data shape:
    // {
    //   externalLinks: []
    //   ?????
    // }
    const { externalLinks = [] } = data;
    const putData = { ...data, externalLinks };
    return new Promise((resolve, reject) =>
      fetch('/api/podcast/metadata', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(putData),
      })
        .then(response => {
          if (response.status === 200) {
            response
              .json()
              .then(responseJSON => {
                resolve(responseJSON, response);
              })
              .catch(err => {
                // Response doesn't include json...
                resolve({}, response);
              });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    );
  },
  updateCoverArtAttribution: (data = {}) =>
    new Promise((resolve, reject) =>
      fetch('/api/podcast/image/attribution', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(data),
      })
        .then(response => {
          if (response.ok) {
            resolve(); // no metadata
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    ),
  // Takes a file blob OR an image url and returns urls to proccessed versions of the image.
  // Returns responseJSON shape:
  // {
  //   podcastImage: string -- url
  //   podcastImage400: string -- url
  // }
  uploadImage: (file, options = {}) => {
    // options shape:
    // {
    //   hasAnchorBranding: bool
    //   isEpisode: bool
    //   doOverrideSize: bool
    // }
    // Check if file represents an image url
    const isFileAUrl = typeof file === 'string';
    const fileKey = isFileAUrl ? 'fileUrl' : 'file';

    const formData = new FormData();
    formData.append(fileKey, file);

    for (const k in options) {
      formData.append(k, options[k]);
    }
    return new Promise((resolve, reject) => {
      fetch('/api/onboarding/image', {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
      })
        .then(response => {
          if (response.status === 200) {
            response
              .json()
              .then(responseJSON => {
                resolve(responseJSON, response);
              })
              .catch(() => {
                reject(new Error(`Response doesnt include json`), response);
              });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  // Returns responseJSON shape:
  // {
  //   url: string -- image url
  // }
  generateImage: podcastName =>
    new Promise((resolve, reject) => {
      fetch(`/api/onboarding/image?title=${encodeURIComponent(podcastName)}`, {
        method: 'GET',
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  publishPodcast: () =>
    new Promise((resolve, reject) => {
      fetch('/api/podcast/publish', {
        method: 'POST',
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.ok) {
            resolve(response);
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  optOutOfPodcastDistribution,
  fetchPodcastEpisode: episodeId =>
    new Promise((resolve, reject) => {
      fetch(`/api/podcastepisode/${episodeId}`, {
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  fetchPodcastEpisodes: (page = null) => {
    const baseUrlPath = '/api/podcastepisode';
    const urlPath = page === null ? baseUrlPath : `${baseUrlPath}?page=${page}`;
    return fetch(urlPath, {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json().then(responseJSON => responseJSON);
        }
        if (response.status === 400) {
          return Promise.reject(new AnchorAPIError(response, `Missing image`));
        }
        return Promise.reject(
          new AnchorAPIError(
            response,
            `Non 200 response status: ${response.status}`
          )
        );
      })
      .catch(err => {
        throw err;
      });
  },
  fetchPodcastStatus,
  fetchMyPodcastMetadata: () =>
    new Promise((resolve, reject) => {
      fetch('/api/podcast/metadata', {
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  fetchPodcastNetworkStations: () =>
    new Promise((resolve, reject) => {
      fetch('/api/user/podcastnetwork/stations', {
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.ok) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  // data shape
  // {
  //   vanitySlug: string
  //   betaCode: string
  //   feedUrl: string
  //   name: string
  //   email: string
  //   password: string
  // }
  createAccount: data =>
    new Promise((resolve, reject) =>
      fetch('/api/user/account/', {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(data),
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    ),
  fetchCoverArtImageChoices: (searchText, offset) =>
    fetch(`/api/coverart?searchQuery=${searchText}&offset=${offset}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    }).then(response => {
      if (response.status === 200) {
        return response.json().then(responseJSON => responseJSON);
      }
      if (response.status === 400) {
        return Promise.reject(new AnchorAPIError(response, `Missing image`));
      }
      return Promise.reject(
        new AnchorAPIError(
          response,
          `Non 200 response status: ${response.status}`
        )
      );
    }),
  fetchCoverArtTextOverlay: ({ podcastName, fontName }) =>
    new Promise((resolve, reject) =>
      fetch('/api/coverart/text', {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          podcastName,
          expectedFonts: [fontName],
        }),
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    ),
  fetchCroppedImage: ({ imageUrl, fileObj, x, y, width, height }) => {
    const data = {
      x,
      y,
      ...(imageUrl ? { imageUrl } : fileObj ? { file: fileObj } : {}),
      width,
      height,
    };
    const formData = convertDataToFormData(data);
    return new Promise((resolve, reject) =>
      fetch('/api/coverart/images/crop', {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    );
  },
  fetchCoverArtColors: imageUrl =>
    new Promise((resolve, reject) =>
      fetch('/api/coverart/extract', {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ imageUrl }),
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    ),
  fetchSplitAudio: ({ webAudioId, splitsAtMilliseconds }) =>
    new Promise((resolve, reject) =>
      fetch(`/api/audio/${webAudioId}/transformation/request/split`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          points: splitsAtMilliseconds.map(splitAtMilliseconds => ({
            offset: splitAtMilliseconds,
          })),
        }),
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    ),
  fetchFinalCoverArtImage: ({
    userId,
    stationId,
    podcastName,
    fontName,
    imageUrl,
    fileObj,
    x,
    y,
    width,
    height,
    fillHex,
    horizontalAlignment,
    verticalAlignment,
    text,
  }) => {
    const data = {
      ...(imageUrl ? { imageUrl } : fileObj ? { file: fileObj } : {}),
      userId,
      stationId,
      podcastName,
      fontName,
      x,
      y,
      width,
      height,
      fillHex,
      horizontalAlignment,
      verticalAlignment,
      text,
    };
    const formData = convertDataToFormData(data);

    return new Promise((resolve, reject) =>
      fetch(
        `/api/coverart/images/final?userId=${userId}&stationId=${stationId}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          body: formData,
        }
      )
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON, response);
            });
          } else if (response.status === 400) {
            reject(new AnchorAPIError(response, `Missing image`));
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        })
    );
  },
  acceptMonetizationTerms: webStationId =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/v3/tos/accept`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          webStationId,
          type: 'monetization',
        }),
      })
        .then(resolve)
        .catch(reject);
    }).then(response => {
      const { status } = response;
      if (status === 200) {
        return Promise.resolve(response.json());
      }
      return Promise.reject(new AnchorAPIError(response, 'Server error.'));
    }),
  updateSponsorshipsPreference: ({
    webStationId,
    sponsorshipsFeaturePreference,
  }) =>
    new Promise((resolve, reject) => {
      function fireActivatedSponsorshipsEvent() {
        if (sponsorshipsFeaturePreference === 'accepted')
          trackEvent('sponsorships_activated', {}, { providers: [mParticle] });
      }
      return fetch(
        `/api/proxy/v3/sponsor/webStationId:${webStationId}/updateSponsorshipsPreference`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            sponsorshipsFeaturePreference,
          }),
        }
      )
        .then(res => {
          if (res.status === 200) fireActivatedSponsorshipsEvent();
          return resolve(res);
        })
        .catch(reject);
    }),
  publishExistingAudioAsAd: ({ adCampaignId, webStationId, webAudioId } = {}) =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/v3/sponsor/webStationId:${webStationId}/existing`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          adCampaignId,
          webAudioId,
        }),
      })
        .then(resolve)
        .catch(reject);
    }).then(response => {
      const { status } = response;
      if (status === 200 || status === 400) {
        return response.json();
      }
      return Promise.reject(new AnchorAPIError(response, 'Server error.'));
    }),
  enableSponsorshipsOnAllEpisodes: webStationId =>
    new Promise((resolve, reject) => {
      fetch(
        `/api/proxy/v3/sponsor/webStationId:${webStationId}/sponsorstate/enableall`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
        }
      )
        .then(resolve)
        .catch(reject);
    }).then(response => {
      if (response.ok) {
        return {};
      }
      return Promise.reject(new AnchorAPIError(response, 'Server error.'));
    }),
  updateEpisodeAdState: ({ webEpisodeId, isAdSlotEnabled } = {}) =>
    new Promise((resolve, reject) => {
      fetch(
        `/api/proxy/v3/sponsor/episode/webEpisodeId:${webEpisodeId}/sponsorstate`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            isEnabled: isAdSlotEnabled,
          }),
        }
      )
        .then(resolve)
        .catch(reject);
    }).then(response => {
      const { status } = response;
      if (status === 200) {
        return response.json();
      }
      throw new AnchorAPIError(
        response,
        `Non 200 response status: ${response.status}`
      );
    }),
  fetchAdCampaignForStation: (campaignId, webStationId) =>
    new Promise((resolve, reject) => {
      fetch(
        `/api/proxy/v3/sponsor/webStationId:${webStationId}/campaigns/${campaignId}`,
        {
          credentials: 'same-origin',
          method: 'GET',
        }
      )
        .then(resolve)
        .catch(reject);
    })
      .then(response => {
        const { status } = response;
        if (status === 200 || status === 400) {
          return response.json();
        }
        return Promise.reject(new AnchorAPIError(response, 'Server error.'));
      })
      .then(responseJson => responseJson),
  fetchAdCompaignsForStation: webStationId =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/v3/sponsor/webStationId:${webStationId}/campaigns`, {
        credentials: 'same-origin',
        method: 'GET',
      })
        .then(resolve)
        .catch(reject);
    })
      .then(response => {
        const { status } = response;
        if (status === 200 || status === 400) {
          return response.json();
        }
        return Promise.reject(new AnchorAPIError(response, 'Server error.'));
      })
      .then(responseJson => responseJson),
  // TODO: BLOCKER FOR RELEASE -- All incremental Ids need to be conversted into `webIds`
  ignoreCampaigns: ({ webStationId, userId, campaignIdsToIgnore }) =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/v3/sponsor/interested`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          webStationId,
          userId,
          interestedAdCampaignIds: [],
          notInterestedAdCampaignIds: campaignIdsToIgnore,
        }),
      })
        .then(response => {
          if (response.status === 200) {
            resolve();
            return;
          }
          reject(
            new AnchorAPIError(
              response,
              `Non 200 response status: ${response.status}`
            )
          );
        })
        .catch(err => {
          reject(err);
        });
    }),
  // TODO: BLOCKER FOR RELEASE -- All incremental Ids need to be conversted into `webIds`
  unIgnoreCampaigns: ({ webStationId, userId, campaignIdsToUnIgnore }) =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/v3/sponsor/interested`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          webStationId,
          userId,
          interestedAdCampaignIds: campaignIdsToUnIgnore,
          notInterestedAdCampaignIds: [],
        }),
      })
        .then(response => {
          if (response.status === 200) {
            resolve();
            return;
          }
          reject(
            new AnchorAPIError(
              response,
              `Non 200 response status: ${response.status}`
            )
          );
        })
        .catch(err => {
          reject(err);
        });
    }),
  deleteCampaign: ({ webStationId, userId, campaignId }) =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/v3/sponsor/delete`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          userId,
          webStationId,
          adCampaignId: campaignId,
        }),
      })
        .then(response => {
          if (response.status === 200) {
            resolve();
            return;
          }
          reject(
            new AnchorAPIError(
              response,
              `Non 200 response status: ${response.status}`
            )
          );
        })
        .catch(err => {
          reject(err);
        });
    }),
  fetchAllEnabledFeatureFlags: (baseUrl = '') =>
    new Promise((resolve, reject) => {
      fetch(`${baseUrl}/api/featureflag/enabled`, {
        credentials: 'same-origin',
      })
        .then(resolve)
        .catch(reject);
    }).then(response => {
      if (response.status === 200) {
        return response.json();
      }
      throw new AnchorAPIError(
        response,
        `Non 200 response status: ${response.status}`
      );
    }),
  fetchMoneyWallet,
  checkAndPersistRedirect: (webStationId, userId) =>
    new Promise((resolve, reject) => {
      fetch(
        `/api/proxy/v3/stations/webStationId:${webStationId}/checkAndPersistRedirect`,
        {
          credentials: 'same-origin',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          method: 'POST',
          body: JSON.stringify({
            userId,
          }),
        }
      )
        .then(resolve)
        .catch(reject);
    }).then(response => {
      if (!response.ok) {
        return new AnchorAPIError(
          response,
          `Non 200 response status: ${response.status}`
        );
      }
      return response.json();
    }),
  fetchMoney: () =>
    new Promise((resolve, reject) => {
      fetch('/api/user/money/status', {
        method: 'GET',
        credentials: 'same-origin',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  joinRWFCall: (
    conferenceCallId,
    inviteCode,
    displayName,
    isLoggedIn,
    emailAddress
  ) =>
    new Promise((resolve, reject) => {
      const body = {
        displayName,
        deviceKind: isIOS() || isAndroidChrome() ? 'mobile' : 'desktop',
        ...(!isLoggedIn ? { emailAddress } : {}),
      };
      fetch(
        isLoggedIn
          ? `/api/proxy/jrtrot/${conferenceCallId}/join`
          : `/api/proxy/jrtrot/${inviteCode}/joinanon`,
        {
          credentials: 'same-origin',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          method: 'POST',
          body: JSON.stringify(body),
        }
      )
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  registerSessionConnection: ({ conferenceCallId, sessionId, anonUserUUID }) =>
    new Promise((resolve, reject) => {
      const postData = {
        connectionId: sessionId,
        ...(anonUserUUID ? { uuid: anonUserUUID } : {}),
      };
      fetch(`/api/proxy/jrtrot/${conferenceCallId}/connection`, {
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        method: 'POST',
        body: JSON.stringify(postData),
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  fetchConferenceCallInfo: inviteCode =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/jrtrot/invited/${inviteCode}`, {
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        method: 'GET',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),
  fetchConferenceCallParticipants: conferenceCallId =>
    new Promise((resolve, reject) => {
      fetch(`/api/proxy/jrtrot/${conferenceCallId}`, {
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        method: 'GET',
      })
        .then(response => {
          if (response.status === 200) {
            response.json().then(responseJSON => {
              resolve(responseJSON);
            });
          } else {
            reject(
              new AnchorAPIError(
                response,
                `Non 200 response status: ${response.status}`
              )
            );
          }
        })
        .catch(err => {
          reject(err);
        });
    }),

  fetchEpisodes,
  fetchUserDeletionRequest,
  requestUserDeletion,
  deleteEpisode,
  fetchCurrentUsersPodcast,
  fetchCurrentPodcastsPlayData,
  fetchCurrentPodcastsWallet,
  fetchCurrentPodcastPlaysByDateRange,
  fetchCurrentPodcastsTopPlayedEpisodes,
  fetchCurrentPodcastsPlaysByApp,
  fetchCurrentPodcastsPlaysByDevice,
  fetchCurrentPodcastsPlaysByGeo,
  fetchEpisodeTotalPlayCount,
  fetchEpisodePlaysByDateRange,
  fetchEpisodePlaysComparison,
  fetchLogin,
  fetchSignupAndLogin,
  updateCurrentUserEmail,
  createVoiceMessage,
  logoutCurrentUser,
  fetchAudioGroup,
  fetchBackgroundTracks,
  fetchAudioLibrary,
  transformAudioWithBackgroundTrack,
  fetchBackgroundTrackOfAudio,
  retryAudioTransformation,
  fetchPodcastCategories,
  updateTranscription,
  generateVideoTranscriptionRequest,
  fetchVideoTranscriptionRequestStatus,
  hideCallIn,
  updatePodcastMetadata,
  fetchPodcastMetadata,
  updateSocialUrls,
  fetchSocialUrls,
};

export { AnchorAPI as default, AnchorAPI, AnchorAPIError };
